import {
  MESSAGE_MEDIUM_WHATS_APP,
  MESSAGE_MEDIUMS,
  MESSAGE_MEDIUMS_WITH_EMBEDDED,
} from 'constants/messageMediums';
import { GraphQLCampaign } from 'modules/campaignIndex/domainTypes';
import { getCampaignLink } from 'modules/campaignIndex/utils';
import { ImageWrapper } from 'modules/templateIndex/components/TableView.styles';
import { TemplatePreview } from 'modules/templatePreview';
import { useState } from 'react';
import styled from 'styled-components';
import {
  SendEmail,
  Embedded24 as SendEmbedded,
  SendInApp,
  SendMultiChannel,
  SendPush,
  SendSMS,
  SendWebPush,
  WhatsAppLine24,
} from 'ui/icons';
import { DiamondLogo, LogoColors } from 'ui/logo';
import { colors, spacing } from 'ui/theme';

export const StyledImageWrapper = styled(ImageWrapper)`
  margin-right: ${spacing.base};
  flex-shrink: 0;
`;

export const PreviewImg = styled.img`
  display: block;
  height: 100%;
  width: 100%;
`;

export const StyledLogo = styled(DiamondLogo)`
  opacity: 30%;
  && {
    background: ${colors.white};
  }
`;

type MessageMediumIconProps = {
  messageMedium: string;
};

export const MessageMediumIcon = ({ messageMedium }: MessageMediumIconProps) => {
  switch (messageMedium) {
    case MESSAGE_MEDIUMS.PUSH.dbKey:
      return <SendPush color={colors.grey25} data-test={`${messageMedium}_icon`} />;
    case MESSAGE_MEDIUMS.WEB_PUSH.dbKey:
      return <SendWebPush color={colors.grey25} data-test={`${messageMedium}_icon`} />;
    case MESSAGE_MEDIUMS.IN_APP.dbKey:
      return <SendInApp color={colors.grey25} data-test={`${messageMedium}_icon`} />;
    case MESSAGE_MEDIUMS.EMAIL.dbKey:
      return <SendEmail color={colors.grey25} data-test={`${messageMedium}_icon`} />;
    case MESSAGE_MEDIUMS.SMS.dbKey:
      return <SendSMS color={colors.grey25} data-test={`${messageMedium}_icon`} />;
    case MESSAGE_MEDIUMS_WITH_EMBEDDED.Embedded.dbKey:
      return <SendEmbedded color={colors.grey25} data-test={`${messageMedium}_icon`} />;
    case MESSAGE_MEDIUM_WHATS_APP.WhatsApp.dbKey:
      return <WhatsAppLine24 color={colors.grey25} data-test={`${messageMedium}_icon`} />;
    default:
      return <SendMultiChannel color={colors.grey25} data-test={`MultiChannel_icon`} />;
  }
};

type CampaignPreviewImageProps = {
  campaign: Pick<GraphQLCampaign, 'campaignState' | 'messageMedium' | 'templateId'>;
  previewThumbnail?: string;
};

export const IMAGE_PREVIEW_MEDIUMS = [MESSAGE_MEDIUMS.EMAIL.dbKey];
export const IFRAME_PREVIEW_MEDIUMS = [MESSAGE_MEDIUMS.IN_APP.dbKey];

/** Gets the preview image to show as the thumbnail for a template */
export const CampaignPreviewThumbnail = ({
  campaign,
  previewThumbnail,
}: CampaignPreviewImageProps) => {
  const [showFallbackImage, setShowFallbackImage] = useState(false);
  const campaignLink = getCampaignLink(campaign as GraphQLCampaign);
  const { messageMedium, templateId } = campaign;

  if (IMAGE_PREVIEW_MEDIUMS.includes(messageMedium)) {
    return previewThumbnail ? (
      <a data-test="preview-icon" href={campaignLink}>
        <StyledImageWrapper>
          {showFallbackImage ? (
            <MessageMediumIcon messageMedium={messageMedium} />
          ) : (
            <PreviewImg
              alt="preview"
              onError={() => setShowFallbackImage(true)}
              src={previewThumbnail}
            />
          )}
        </StyledImageWrapper>
      </a>
    ) : (
      <a data-test="preview-icon" href={campaignLink}>
        <StyledImageWrapper isEmptyState>
          <StyledLogo color={LogoColors.COLOR} height={20} />
        </StyledImageWrapper>
      </a>
    );
  }

  if (IFRAME_PREVIEW_MEDIUMS.includes(messageMedium)) {
    return (
      <TemplatePreview size="thumbnail" templateId={templateId} messageMedium={messageMedium} />
    );
  }

  return (
    <a data-test="preview-icon" href={campaignLink}>
      <StyledImageWrapper>
        <MessageMediumIcon messageMedium={messageMedium} />
      </StyledImageWrapper>
    </a>
  );
};
